import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../services/api';

export interface SystemMetrics {
  cpu: number;
  memory: number;
  disk: number;
  network: {
    bytesIn: number;
    bytesOut: number;
  };
  apiResponseTime: number;
  activeUsers: number;
  queueSize: number;
  errorRate: number;
  lastUpdated: string;
}

export interface Alert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: string;
  resolved: boolean;
}

interface MonitorState {
  systemMetrics: SystemMetrics | null;
  alerts: Alert[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: MonitorState = {
  systemMetrics: null,
  alerts: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

export const fetchSystemMetrics = createAsyncThunk(
  'monitor/fetchSystemMetrics',
  async () => {
    const response = await api.get('/monitor/metrics');
    return response.data;
  }
);

export const fetchAlerts = createAsyncThunk(
  'monitor/fetchAlerts',
  async (params: { limit?: number; type?: string; resolved?: boolean } = {}) => {
    const response = await api.get('/monitor/alerts', { params });
    return response.data;
  }
);

export const resolveAlert = createAsyncThunk(
  'monitor/resolveAlert',
  async (alertId: string) => {
    const response = await api.post(`/monitor/alerts/${alertId}/resolve`);
    return response.data;
  }
);

const monitorSlice = createSlice({
  name: 'monitor',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateSystemMetrics: (state, action) => {
      state.systemMetrics = { ...state.systemMetrics, ...action.payload };
      state.lastUpdated = new Date().toISOString();
    },
    addAlert: (state, action) => {
      state.alerts.unshift(action.payload);
      // 保持最多100条告警
      if (state.alerts.length > 100) {
        state.alerts = state.alerts.slice(0, 100);
      }
    },
    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.systemMetrics = action.payload.data;
        state.lastUpdated = action.payload.timestamp || new Date().toISOString();
      })
      .addCase(fetchSystemMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取系统指标失败';
      })
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload.data || [];
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取告警数据失败';
      })
      .addCase(resolveAlert.fulfilled, (state, action) => {
        const alertId = action.payload.alertId;
        const alert = state.alerts.find(a => a.id === alertId);
        if (alert) {
          alert.resolved = true;
        }
      });
  },
});

export const { clearError, updateSystemMetrics, addAlert, removeAlert } = monitorSlice.actions;
export default monitorSlice.reducer;










